/**
 * React Query mutation hooks for arable fields.
 * Handles create, update, and delete operations.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import {
  ArableField,
  ArableFieldResponse,
  prepareArableFieldRequest,
  processArableFieldResponse,
} from "model/arableFieldTypes";
import { clearTilesFromCache } from "services";
import { Farm } from "./useFarmQueries";

const arableFieldApi = new API<ArableFieldResponse>("fields");

/**
 * React Query mutation hook for creating an arable field
 */
export function useCreateArableField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arableField: ArableField) => {
      const res = await arableFieldApi.createOne(
        prepareArableFieldRequest(arableField)
      );
      const newField = await arableFieldApi.getOne(res.guid);
      const processed = processArableFieldResponse([newField]);
      return processed[0];
    },
    onSuccess: async (newField) => {
      // Invalidate and refetch arable fields for the farm
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.detail(newField.guid),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.all,
      });
      if (newField.farm) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.arableFields.ids(newField.farm),
        });
      }
    },
    onError(error) {
      console.error("Error creating arable field:", error);
    },
  });
}

/**
 * React Query mutation hook for updating an arable field
 */
export function useUpdateArableField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arableField: ArableField) => {
      await arableFieldApi.updateOne(prepareArableFieldRequest(arableField));
      const updatedField = await arableFieldApi.getOne(arableField.guid);
      const processed = processArableFieldResponse([updatedField]);
      return processed[0];
    },
    onMutate: async (arableField) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.arableFields.detail(arableField.guid),
      });

      // Snapshot previous value
      const previousField = queryClient.getQueryData<ArableField>(
        queryKeys.arableFields.detail(arableField.guid)
      );

      // Optimistically update the geometry
      if (previousField) {
        queryClient.setQueryData<ArableField>(
          queryKeys.arableFields.detail(arableField.guid),
          {
            ...previousField,
            geom: arableField.geom,
          }
        );
      }

      return { previousField };
    },
    onError: (_err, arableField, context) => {
      // Rollback on error
      if (context?.previousField) {
        queryClient.setQueryData(
          queryKeys.arableFields.detail(arableField.guid),
          context.previousField
        );
      }
    },
    onSuccess: async (updatedField) => {
      // Invalidate and refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.detail(updatedField.guid),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.all,
      });
    },
  });
}

/**
 * React Query mutation hook for deleting an arable field
 */
export function useDeleteArableField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arableField: ArableField) => {
      const res = await arableFieldApi.deleteOne(arableField.guid);
      if (!res.ok) {
        throw new Error(`Failed to delete arable field: ${res.status}`);
      }
      clearTilesFromCache(arableField.guid);
      return { arableFieldGuid: arableField.guid, farmGuid: arableField.farm };
    },
    onSuccess: async ({ arableFieldGuid, farmGuid }) => {
      console.log("Arable field deleted:", arableFieldGuid);
      // Remove field IDs cache entry synchronously to prevent refetch of deleted field
      // This MUST happen before invalidateQueries to avoid race conditions where
      // useArableFields still has the old field ID and tries to fetch it
      if (farmGuid) {
        const currentIds = queryClient.getQueryData<string[]>(
          queryKeys.arableFields.ids(farmGuid)
        );
        if (currentIds) {
          queryClient.setQueryData<string[]>(
            queryKeys.arableFields.ids(farmGuid),
            currentIds.filter((id) => id !== arableFieldGuid)
          );
        }
      }

      // Remove the detail query from cache
      queryClient.removeQueries({
        queryKey: queryKeys.arableFields.detail(arableFieldGuid),
      });

      // Update parent farm's fields array in React Query cache
      if (farmGuid) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(farmGuid)
        );
        if (farm) {
          queryClient.setQueryData<Farm>(queryKeys.farms.detail(farmGuid), {
            ...farm,
            fields: (farm.fields || []).filter((id) => id !== arableFieldGuid),
          });
        }
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.all,
      });

      if (farmGuid) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.arableFields.ids(farmGuid),
        });
      }
    },
  });
}
